import React, { useEffect, useState } from 'react';
import api from '../services/api';
import type StudyModel from '../models/StudyModel';
import LoadingSpinner from './LoadingSpinner';

type PropTypes = {
    selectedStudy: StudyModel;
    setSelectedStudy: (selectedStudy: StudyModel) => void;
};

function StudySearchField({ selectedStudy, setSelectedStudy }: PropTypes) {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([] as StudyModel[]);
    const [searching, setSearching] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const pullResults = async () => {
        const { studies } = await api.getStudies(searchTerm);
        setSearchResults(studies);
    };

    useEffect(() => {
        if (searchTerm) {
            setSearching(true);
            const delayDebounceFn = setTimeout(async () => {
                await pullResults();
                setSearching(false);
            }, 500);

            return () => {
                clearTimeout(delayDebounceFn);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const handleSelect = (result: StudyModel) => {
        setSelectedStudy(result);
        setSearchTerm(result.name);
    };

    const onFocus = async () => {
        await pullResults();
        setIsFocused(true);
    };

    const onBlur = () => {
        setTimeout(() => {
            setIsFocused(false);
        }, 250);
    };

    return (
        <div className="flex items-center border relative border-black rounded p-1 w-1/2">
            <input
                className="text-primary w-full outline-0"
                type="text"
                name="study"
                value={isFocused ? searchTerm : selectedStudy.name}
                onChange={(event) => {
                    setSearchTerm(event.target.value);
                }}
                onFocus={onFocus}
                onBlur={onBlur}
            />
            {searching && <LoadingSpinner size={15} />}
            {isFocused && (
                <div className="border border-slate-300 rounded absolute bg-white text-primary w-full top-9 z-10">
                    {searchResults?.map((result: StudyModel, index: number) => {
                        return (
                            <div
                                key={result.id}
                                id={`search-result-${index}`}
                                className="cursor-pointer hover:bg-slate-100 p-1"
                                onClick={() => {
                                    handleSelect(result);
                                }}
                            >
                                {result.name}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default StudySearchField;
